import { Any } from '~/common/utils';
import { ZIP_LIMITS, MAX_COMPRESSION_RATIO, DANGEROUS_EXTENSIONS } from '../constants';
import { getFileExtension } from '../fileTypeDetection';
import { FileInput, ValidationResult } from '../types';
import { checkMagicBytes, formatBytes } from '../utils';
import { ZipParser, ZipEntryMetadata } from '../ZipParser';
import { validateImage } from './validateImage';
import { validatePdf } from './validatePdf';
import { validatePpt } from './validatePpt';
import { validatePptx } from './validatePptx';

export async function validateZip(input: FileInput): Promise<ValidationResult> {
  if (!(input instanceof File)) {
    return { valid: true };
  }

  if (input.size < ZIP_LIMITS.min)
    return { valid: false, reason: `Too small — minimum valid ZIP is 22 bytes` };
  if (input.size > ZIP_LIMITS.max)
    return { valid: false, reason: `Exceeds max size (${formatBytes(ZIP_LIMITS.max)})` };

  const { isPK } = await checkMagicBytes(input);
  if (!isPK) return { valid: false, reason: 'Invalid ZIP header — missing PK signature' };

  const parser = new ZipParser(input);
  let entries: ZipEntryMetadata[];
  let namedEntries: Record<string, ZipEntryMetadata>;

  try {
    const parseResult = await parser.parse();
    entries = parseResult.entries;
    namedEntries = parseResult.namedEntries;
  } catch (e) {
    return { valid: false, reason: `ZIP structure invalid: ${(e as Error).message}` };
  }

  // Check for valid files (exclude directories and __MACOSX)
  const validFiles = entries.filter(
    (e) => !e.isDirectory && !e.name.includes('__MACOSX') && !e.name.endsWith('/'),
  );

  if (validFiles.length === 0) {
    return { valid: false, reason: 'ZIP contains no valid files' };
  }

  // Block dangerous file types, path traversal, and Unicode tricks
  for (const entry of validFiles) {
    // Check for Unicode RLO/bidi overrides (U+202E, U+202D, U+202C, etc.)
    const hasBidiOverride = /[\u202A-\u202E\u2066-\u2069]/u.test(entry.name);
    if (hasBidiOverride) {
      return {
        valid: false,
        reason: `Security risk: "${entry.name}" contains Unicode bidirectional override characters`,
      };
    }

    // Check for path traversal (zip-slip)
    if (entry.name.includes('../') || entry.name.includes('..\\') || entry.name.startsWith('/')) {
      return {
        valid: false,
        reason: `Security risk: "${entry.name}" contains path traversal`,
      };
    }

    const ext = getFileExtension(entry.name);

    if (ext && DANGEROUS_EXTENSIONS.includes(ext as Any)) {
      return {
        valid: false,
        reason: `Security risk: "${entry.name}" contains prohibited file type (.${ext})`,
      };
    }
  }

  const totalUncompressed = entries.reduce((sum, e) => sum + e.uncompressedSize, 0);

  if (input.size > 0 && totalUncompressed / input.size > MAX_COMPRESSION_RATIO)
    return {
      valid: false,
      reason: `Suspicious compression ratio (${(totalUncompressed / input.size).toFixed(0)}x)`,
    };

  // Process all entries sequentially (no yielding in worker context)
  const validEntries = entries.filter(
    (entry) => !entry.isDirectory && entry.compressedSize > 0 && !entry.name.includes('__MACOSX'),
  );

  // Cache parsed PPTX entries to avoid re-parsing later
  const pptxCache: Record<string, ZipEntryMetadata[]> = {};

  for (const entry of validEntries) {
    // Validate CRC-32 and get magic bytes in single pass (no yielding in worker)
    const validationResult = await parser.validateAndGetMagicBytes(entry, 16);
    if (!validationResult.valid) {
      return {
        valid: false,
        reason: `"${entry.name}": ${validationResult.reason}`,
      };
    }

    const magicBytes = validationResult.magicBytes!;
    const magicView = new DataView(magicBytes.buffer, magicBytes.byteOffset, magicBytes.byteLength);

    // Check for MZ header (0x4D5A)
    if (magicBytes.length >= 2 && magicView.getUint16(0, false) === 0x4d5a) {
      return {
        valid: false,
        reason: `Security risk: "${entry.name}" is an executable file`,
      };
    }

    // File-type specific validation with streaming support
    const ext = getFileExtension(entry.name);
    if (!ext) continue;

    const fileInput = { entry, parser, name: entry.name };
    let typeValidation: ValidationResult | null = null;

    switch (ext) {
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
        typeValidation = await validateImage(fileInput);
        break;

      case 'pdf':
        typeValidation = await validatePdf(fileInput);
        break;

      case 'ppt':
        typeValidation = await validatePpt(fileInput);
        break;

      case 'pptx':
        typeValidation = await validatePptx(fileInput);
        // Merge PPTX cache from validation
        if (typeValidation.valid && typeValidation.pptxCache) {
          Object.assign(pptxCache, typeValidation.pptxCache);
        }
        break;
    }

    if (typeValidation && typeValidation.valid === false) {
      return {
        valid: false,
        reason: `"${entry.name}": ${typeValidation.reason}`,
      };
    }
  }

  return { valid: true, entries: namedEntries, pptxCache };
}
